import type { WorldRoomDirectory } from "@cleo/contracts";

import { moreUrgentRootSessionStatus, type RootSessionStatus } from "../rootSessionStrip";
import type { SessionLivenessRollup } from "./worldLiveness";
import { projectWorldSessionLineage, projectWorldSessionRows } from "./worldView";

// NV4 — the hidden-attention projection: which Sessions carry work that cannot
// proceed without a person, whose tabs the addressed view does not currently
// render. "Hidden" is exactly the tab rows' own visibility law inverted — a
// Session is visible when it sits in a rendered row as an engaged tab (or is
// addressed while headless, the NV2 exception); everything else is behind the
// current view. The fold is display-and-navigation input only: it reads the
// same index and liveness rollups the strip already reads, stores nothing,
// and writes nothing (DL3/DL4). Ancestors and visible siblings never appear
// here — their own tab dots already answer for them; this projection exists
// solely so a person three levels behind an unselected tab is never invisible
// while blocked.

/**
 * The needs-a-person statuses: the top of the urgency ladder, where work is
 * stopped until someone acts. working/monitoring proceed alone and done is
 * completion — those stay on their tabs' dots and never lift.
 */
export const NEEDS_A_PERSON_STATUSES: ReadonlySet<RootSessionStatus> = new Set([
  "failed",
  "approval",
  "input",
]);

export interface HiddenAttentionEntry {
  readonly sessionId: string;
  readonly label: string;
  readonly status: RootSessionStatus;
}

/**
 * Fold the index + liveness rollups into the hidden needs-a-person list, most
 * urgent first (ties keep index order, which is birth order). One entry per
 * Session, carrying its OWN status — a deep session's plea appears on its own
 * Session, never re-attributed to an ancestor (the ghost dots already do that).
 */
export function projectHiddenAttention(input: {
  readonly index: WorldRoomDirectory;
  readonly activeSessionId: string;
  readonly rollups: ReadonlyMap<string, SessionLivenessRollup>;
}): ReadonlyArray<HiddenAttentionEntry> {
  const lineage = new Set(
    projectWorldSessionLineage(input.index, input.activeSessionId).map((slice) => slice.sessionId),
  );
  const visible = new Set<string>();
  for (const row of projectWorldSessionRows(input.index, input.activeSessionId)) {
    for (const slice of row.sessions) {
      if (slice.engagement !== "headless" || lineage.has(slice.sessionId)) {
        visible.add(slice.sessionId);
      }
    }
  }
  const entries: HiddenAttentionEntry[] = [];
  for (const slice of input.index.sessions) {
    if (visible.has(slice.sessionId)) continue;
    const own = input.rollups.get(slice.sessionId)?.own ?? null;
    if (own === null || !NEEDS_A_PERSON_STATUSES.has(own)) continue;
    entries.push({ sessionId: slice.sessionId, label: slice.label, status: own });
  }
  return entries.sort((left, right) =>
    left.status === right.status
      ? 0
      : moreUrgentRootSessionStatus(left.status, right.status) === left.status
        ? -1
        : 1,
  );
}
